import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DateTime } from 'luxon';
import * as React from 'react';
import { Button } from 'src/shadcn/components/ui/button';
import { Calendar } from 'src/shadcn/components/ui/calendar';
import { FormControl } from 'src/shadcn/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shadcn/components/ui/popover';
import { ScrollArea } from 'src/shadcn/components/ui/scroll-area';
import { cn } from 'src/shadcn/lib/utils';

export interface IAppointmentDatePickerProps {
  fieldValue: Date | undefined;
  setFieldValue: (value: Date | undefined) => void;
}

export const AppointmentDatePicker: React.FunctionComponent<
  IAppointmentDatePickerProps
> = ({ fieldValue, setFieldValue }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant={'outline'}
            className={cn(
              'w-full pl-3 text-left font-normal',
              !fieldValue && 'text-muted-foreground'
            )}
          >
            {fieldValue ? (
              format(fieldValue, 'MMM d, yyyy h:mma')
            ) : (
              <span>Pick a date</span>
            )}
            <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <div className='flex flex-col gap-5 overflow-hidden md:flex-row'>
          <Calendar
            mode='single'
            selected={fieldValue}
            onSelect={newDate => {
              // TODO add time selection.
              if (newDate) {
                newDate = DateTime.fromJSDate(newDate)
                  .startOf('day')
                  .plus({ hours: 9 })
                  .toJSDate();
              }
              setFieldValue(newDate);
            }}
            disabled={date =>
              date < DateTime.now().minus({ days: 1 }).endOf('day').toJSDate()
            }
            initialFocus
          />
          <ScrollArea className='h-[305px] max-h-[305px] overflow-hidden'>
            <div className='flex flex-col gap-2 p-2'>
              {getDates(DateTime.fromJSDate(fieldValue || new Date())).map(
                d => {
                  return (
                    <Button
                      key={d.toMillis()}
                      variant={'outline'}
                      onClick={() => {
                        setFieldValue(d.toJSDate());
                        setOpen(false);
                      }}
                      className={cn({
                        'bg-gray-100':
                          d.valueOf() ===
                          DateTime.fromJSDate(
                            fieldValue || new Date()
                          ).valueOf(),
                      })}
                    >
                      {format(d.toJSDate(), 'p')}
                    </Button>
                  );
                }
              )}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
};

function getDates(dayForTimeSelection: DateTime) {
  const minuteInterval = 30;
  const openingHour = 8; // TODO: this needs to be opening hours
  const totalHoursOfOperationThisDay = 12; // TODO: this needs to be total hours of operation
  const daysStart = dayForTimeSelection
    .startOf('day')
    .plus({ hours: openingHour });
  const dates = [];
  for (
    let i = 0;
    i < totalHoursOfOperationThisDay * (60 / minuteInterval);
    i++
  ) {
    dates.push(daysStart.plus({ minutes: i * minuteInterval }));
  }
  return dates;
}
